import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../extra/Button";
import Table from "../../extra/Table";
import Title from "../../extra/Title";
import Pagination from "../../extra/Pagination";
import { thumb } from "../../../util/thumb";
import { useDispatch, useSelector } from "react-redux";
import { getAffiliateRequest, acceptAffiliateRequest, rejectAffiliateRequest } from "../../store/affiliateRequest/affiliateRequest.action";
import { baseURL } from "../../../util/config";
import $ from "jquery";
import dayjs from "dayjs";
import { InfoOutlined } from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { OPEN_DIALOGUE } from "../../store/dialogue/dialogue.type";
import AffiliateDetailsDialog from "./AffiliateDetailsDialog";
import { colors } from "../../../util/SkeletonColor";
import { alert, warning } from "../../../util/Alert";

const AffiliateRequest = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [search, setSearch] = useState("");

  const { affiliateRequest } = useSelector((state) => state.affiliateRequest);
  const { dialogue, dialogueType } = useSelector((state) => state.dialogue);
  const hasPermission = useSelector((state) => state.admin.admin.flag);

  useEffect(() => {
    dispatch(getAffiliateRequest(page, size, search));
  }, [dispatch, page, size, search]);

  const handleAccept = (id) => {
    console.log("handleAccept called with id:", id);
    console.log("hasPermission:", hasPermission);

    const data = warning();
    data
      .then((result) => {
        console.log("Warning result:", result);
        if (result) {
          console.log("Confirming approval for id:", id);
          dispatch(acceptAffiliateRequest(id))
            .then(() => {
              alert("Success", "Creator request approved successfully!", "success");
              // Refresh the list after approval
              setTimeout(() => {
                dispatch(getAffiliateRequest(page, size, search));
              }, 1000);
            })
            .catch((err) => {
              console.error("Error accepting request:", err);
              alert("Error", "Failed to approve creator request", "error");
            });
        }
      })
      .catch((err) => console.log("Error in warning dialog:", err));
  };

  const handleReject = (id) => {
    const data = warning();
    data
      .then((result) => {
        if (result) {
          dispatch(rejectAffiliateRequest(id))
            .then(() => {
              alert("Success", "Creator request deleted successfully!", "success");
              setTimeout(() => {
                dispatch(getAffiliateRequest(page, size, search));
              }, 1000);
            })
            .catch((err) => {
              console.error("Error deleting request:", err);
              alert("Error", "Failed to delete creator request", "error");
            });
        }
      })
      .catch((err) => console.log(err));
  };

  const handleOpenDialog = (data) => {
    dispatch({
      type: OPEN_DIALOGUE,
      payload: { type: "AffiliateDetails", data },
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Pagination hands over the chosen number itself, not a DOM event.
  const handleChangeRowsPerPage = (value) => {
    setSize(parseInt(value, 10) || 25);
    setPage(1);
  };

  // Table configuration
  const mapData = [
    {
      Header: "No",
      width: "5%",
      Cell: ({ index }) => <span>{(page - 1) * size + index + 1}</span>,
    },
    {
      Header: "User",
      width: "15%",
      Cell: ({ row }) => (
        <div className="d-flex align-items-center">
          <img
            src={thumb(row?.image || row?.logo) || "/dummy.png"}
            loading="lazy"
            decoding="async"
            alt="user"
            height={40}
            width={40}
            className="rounded-circle me-2"
            style={{ width: 40, height: 40, minWidth: 40, objectFit: "cover" }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/dummy.png";
            }}
          />
          <div>
            <div className="fw-bold">{row?.firstName} {row?.lastName}</div>
            <small className="text-muted">@{row?.businessTag}</small>
          </div>
        </div>
      ),
    },
    {
      Header: "Store Name",
      width: "15%",
      Cell: ({ row }) => <span>{row?.storeName || "N/A"}</span>,
    },
    {
      Header: "Category",
      width: "10%",
      Cell: ({ row }) => <span>{row?.category || "N/A"}</span>,
    },
    {
      Header: "Mobile",
      width: "12%",
      Cell: ({ row }) => (
        <span>{row?.countryCode} {row?.mobileNumber}</span>
      ),
    },
    {
      Header: "Email",
      width: "15%",
      Cell: ({ row }) => <span>{row?.email}</span>,
    },
    {
      Header: "Request Date",
      width: "10%",
      Cell: ({ row }) => (
        <span>{dayjs(row?.createdAt).format("DD MMM YYYY")}</span>
      ),
    },
    {
      Header: "Status",
      width: "8%",
      Cell: ({ row }) => (
        <span className={`badge ${row?.isAccepted ? "bg-success" : "bg-warning"}`}>
          {row?.isAccepted ? "Approved" : "Pending"}
        </span>
      ),
    },
    {
      Header: "Action",
      width: "10%",
      Cell: ({ row }) => (
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-info"
            onClick={() => handleOpenDialog(row)}
            title="View Details"
          >
            <InfoOutlined fontSize="small" />
          </button>
          {!row?.isAccepted && (
            <>
              <button
                className="btn btn-sm btn-success"
                onClick={() => handleAccept(row?._id)}
                title="Accept"
              >
                <CheckCircleIcon fontSize="small" />
              </button>
              <button
                className="btn btn-sm btn-danger"
                onClick={() => handleReject(row?._id)}
                title="Reject"
              >
                <CancelIcon fontSize="small" />
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="mainCategory">
        <Title
          name="Creator Requests"
          total={affiliateRequest?.total || 0}
          totalShow
        />
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-body">
                <div className="row mb-3">
                  <div className="col-md-6">
                    <h6 className="mb-0">Pending Creator Requests</h6>
                  </div>
                  <div className="col-md-6">
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by name, email, store..."
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                      />
                    </div>
                  </div>
                </div>

                <Table
                  data={affiliateRequest?.data || []}
                  mapData={mapData}
                  type="server"
                  className="table-responsive"
                />

                <Pagination
                  component="div"
                  type={"server"}
                  count={affiliateRequest?.total || 0}
                  totalData={affiliateRequest?.total || 0}
                  serverPage={page}
                  serverPerPage={size}
                  setCurrentPage={setPage}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[25, 50, 100, 500]}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {dialogue && dialogueType === "AffiliateDetails" && <AffiliateDetailsDialog />}
    </>
  );
};

export default AffiliateRequest;